import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock3, Sparkles, Target, Trophy } from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  flattenProductRoadmapDays,
  getProductRoadmapCurrentDay,
  withProductRoadmapDates,
} from "../utils/buildProductRoadmap";

export default function ProductDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [roadmap, setRoadmap] = useState(null);
  const [hasProAccess, setHasProAccess] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      try {
        setLoading(true);
        const [userSnap, roadmapSnap] = await Promise.all([
          getDoc(doc(db, "users", user.uid)),
          getDoc(doc(db, "product_roadmaps", user.uid)),
        ]);
        setHasProAccess(userSnap.exists() && userSnap.data()?.plan === "PRO");
        setRoadmap(roadmapSnap.exists() ? roadmapSnap.data() : null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  const flatDays = useMemo(() => {
    if (!roadmap?.roadmapRef) return [];
    return withProductRoadmapDates(
      flattenProductRoadmapDays(roadmap.roadmapRef),
      roadmap.consistency?.startedAt,
      roadmap.progress?.completedDays || {}
    );
  }, [roadmap]);

  const stats = useMemo(() => {
    const completed = flatDays.filter((item) => item.completed).length;
    const total = flatDays.length;
    const streak = Object.keys(roadmap?.progress?.completedDays || {}).length;
    const currentIndex = getProductRoadmapCurrentDay(roadmap?.consistency?.startedAt);
    const currentDay = flatDays[Math.max(currentIndex - 1, 0)] || null;

    return {
      total,
      completed,
      remaining: Math.max(total - completed, 0),
      progress: total ? Math.round((completed / total) * 100) : 0,
      streak,
      currentDay,
      upcoming: flatDays.filter((item) => item.status === "upcoming").slice(0, 3),
    };
  }, [flatDays, roadmap]);

  if (loading) {
    return (
      <Page>
        <Shell>
          <LoadingHeroCard>
            <SkeletonPill />
            <SkeletonTitle />
            <SkeletonText $w="68%" />
            <SkeletonText $w="54%" />
          </LoadingHeroCard>

          <LoadingStatsGrid>
            {Array.from({ length: 4 }).map((_, index) => (
              <LoadingStatCard key={index}>
                <SkeletonText $w="48%" />
                <SkeletonValue />
              </LoadingStatCard>
            ))}
          </LoadingStatsGrid>

          <LoadingSectionCard>
            <SkeletonSectionTitle />
            <SkeletonText $w="46%" />
            <SkeletonText $w="82%" />
            <LoadingActionRow>
              <SkeletonButton />
              <SkeletonButton />
            </LoadingActionRow>
          </LoadingSectionCard>
        </Shell>
      </Page>
    );
  }

  if (!roadmap) {
    return (
      <Page>
        <EmptyCard>
          <Sparkles size={18} />
          <h1>No product roadmap found</h1>
          <p>Create your product roadmap first, then use the dashboard to track build momentum.</p>
          <ActionButton onClick={() => navigate("/create-product-roadmap")}>
            Create Product Roadmap
          </ActionButton>
        </EmptyCard>
      </Page>
    );
  }

  if (!hasProAccess) {
    return (
      <Page>
        <EmptyCard>
          <Trophy size={18} />
          <h1>Product dashboard is part of PRO</h1>
          <p>Upgrade to get progress visibility, current build focus, and execution tracking for your product roadmap.</p>
          <ActionButton onClick={() => navigate("/pricing")}>View Pricing</ActionButton>
        </EmptyCard>
      </Page>
    );
  }

  return (
    <Page>
      <Shell>
        <Hero>
          <HeroCard>
            <Tag>
              <Sparkles size={14} />
              Product Dashboard
            </Tag>
            <h1>{roadmap.product?.productName}</h1>
            <p>{roadmap.product?.productIdea}</p>
            <MetaRow>
              <MetaPill>{roadmap.product?.platform}</MetaPill>
              <MetaPill>{roadmap.product?.technology}</MetaPill>
              <MetaPill>{roadmap.product?.timelineWeeks} weeks</MetaPill>
            </MetaRow>
          </HeroCard>
          <SideCard>
            <small>Current feature</small>
            <strong>
              {stats.currentDay?.weekFeatureFocus || stats.currentDay?.title || "Start your roadmap"}
            </strong>
            <p>{stats.currentDay?.objective || "Your current build focus will show here."}</p>
          </SideCard>
        </Hero>

        <StatsGrid>
          <StatCard>
            <label><CalendarDays size={14} /> Total days</label>
            <strong>{stats.total}</strong>
          </StatCard>
          <StatCard>
            <label><CheckCircle2 size={14} /> Completed</label>
            <strong>{stats.completed}</strong>
          </StatCard>
          <StatCard>
            <label><Target size={14} /> Progress</label>
            <strong>{stats.progress}%</strong>
          </StatCard>
          <StatCard>
            <label><Clock3 size={14} /> Remaining</label>
            <strong>{stats.remaining}</strong>
          </StatCard>
        </StatsGrid>

        <Section>
          <SectionTitle>Today’s build</SectionTitle>
          <FocusCard>
            <div>
              <strong>{stats.currentDay?.title || "No active day yet"}</strong>
              <p>{stats.currentDay?.deliverable || "Start your self internship to unlock today’s work."}</p>
            </div>
            <ActionRow>
              <SecondaryButton onClick={() => navigate("/my-product-roadmap")}>
                Open Roadmap
              </SecondaryButton>
              {stats.currentDay && (
                <PrimaryButton
                  onClick={() =>
                    navigate(`/product-tasks-board/day?dayId=${stats.currentDay.id}`)
                  }
                >
                  Open Task Board
                </PrimaryButton>
              )}
            </ActionRow>
          </FocusCard>
        </Section>

        <Section>
          <SectionTitle>Upcoming product days</SectionTitle>
          <UpcomingGrid>
            {stats.upcoming.map((item) => (
              <UpcomingCard key={item.id}>
                <span>Day {item.absoluteIndex + 1}</span>
                <strong>{item.title}</strong>
                <p>{item.weekFeatureFocus || item.topic}</p>
              </UpcomingCard>
            ))}
          </UpcomingGrid>
        </Section>
      </Shell>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  padding: 116px 20px 64px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
  color: #e2e8f0;
`;
const Shell = styled.div`max-width: 1140px; margin: 0 auto;`;
const EmptyCard = styled.div`
  max-width: 760px; margin: 0 auto; padding: 28px; border-radius: 28px;
  background: rgba(15, 23, 42, 0.78); border: 1px solid rgba(148,163,184,0.14);
  h1 { margin: 14px 0 0; color:#f8fafc; }
  p { margin: 12px 0 0; color:#94a3b8; line-height:1.7; }
`;
const ActionButton = styled.button`
  margin-top:20px; padding:12px 18px; border:none; border-radius:999px; background:linear-gradient(135deg,#f59e0b,#d97706); color:#111827; font-weight:900; cursor:pointer;
`;
const Hero = styled.div`
  display:grid; grid-template-columns:minmax(0,1.5fr) minmax(280px,0.9fr); gap:18px;
  @media (max-width: 900px){grid-template-columns:1fr;}
`;
const HeroCard = styled.div`
  padding:28px; border-radius:28px; background:rgba(15,23,42,0.78); border:1px solid rgba(148,163,184,0.14);
  h1 { margin:16px 0 0; color:#f8fafc; font-size:36px; }
  p { margin:12px 0 0; color:#94a3b8; line-height:1.7; }
`;
const SideCard = styled.div`
  padding:24px; border-radius:24px; background:rgba(15,23,42,0.78); border:1px solid rgba(148,163,184,0.14);
  small { color:#7dd3fc; font-size:11px; font-weight:900; text-transform:uppercase; }
  strong { display:block; margin-top:10px; color:#f8fafc; font-size:24px; }
  p { margin-top:10px; color:#94a3b8; line-height:1.7; }
`;
const Tag = styled.div`
  display:inline-flex; align-items:center; gap:8px; padding:8px 12px; border-radius:999px; background:rgba(56,189,248,0.1); border:1px solid rgba(56,189,248,0.18); color:#7dd3fc; font-size:12px; font-weight:900;
`;
const MetaRow = styled.div`display:flex; flex-wrap:wrap; gap:10px; margin-top:16px;`;
const MetaPill = styled.div`
  padding:8px 12px; border-radius:999px; background:rgba(15,23,42,0.58); border:1px solid rgba(148,163,184,0.14); color:#e2e8f0; font-size:12px; font-weight:800;
`;
const StatsGrid = styled.div`
  margin-top:20px; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px;
  @media (max-width: 920px){grid-template-columns:repeat(2,minmax(0,1fr));}
  @media (max-width: 560px){grid-template-columns:1fr;}
`;
const StatCard = styled.div`
  padding:18px; border-radius:22px; background:rgba(15,23,42,0.7); border:1px solid rgba(148,163,184,0.12);
  label { display:flex; align-items:center; gap:8px; color:#94a3b8; font-size:12px; font-weight:800; }
  strong { display:block; margin-top:10px; color:#f8fafc; font-size:28px; }
`;
const Section = styled.div`margin-top: 22px;`;
const SectionTitle = styled.h2`color:#f8fafc; font-size:22px;`;
const FocusCard = styled.div`
  margin-top: 12px; padding:20px; border-radius:24px; background:rgba(15,23,42,0.68); border:1px solid rgba(148,163,184,0.12);
  strong { color:#f8fafc; font-size:20px; }
  p { margin-top:10px; color:#94a3b8; line-height:1.7; }
`;
const ActionRow = styled.div`display:flex; flex-wrap:wrap; gap:10px; margin-top:16px;`;
const PrimaryButton = styled.button`
  border:none; border-radius:999px; padding:12px 16px; background:linear-gradient(135deg,#f59e0b,#d97706); color:#111827; font-weight:900; cursor:pointer;
`;
const SecondaryButton = styled.button`
  border:1px solid rgba(148,163,184,0.16); border-radius:999px; padding:12px 16px; background:rgba(148,163,184,0.12); color:#e2e8f0; font-weight:900; cursor:pointer;
`;
const UpcomingGrid = styled.div`
  margin-top:12px; display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:14px;
  @media (max-width: 980px){grid-template-columns:1fr;}
`;
const UpcomingCard = styled.div`
  padding:18px; border-radius:22px; background:rgba(15,23,42,0.68); border:1px solid rgba(148,163,184,0.12);
  span { display:block; color:#7dd3fc; font-size:11px; font-weight:900; text-transform:uppercase; }
  strong { display:block; margin-top:10px; color:#f8fafc; line-height:1.5; }
  p { margin-top:8px; color:#94a3b8; line-height:1.7; }
`;

const shimmer = `
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.08) 0%,
    rgba(148, 163, 184, 0.18) 50%,
    rgba(148, 163, 184, 0.08) 100%
  );
  background-size: 200% 100%;
  animation: loadingShimmer 1.4s ease-in-out infinite;
`;

const LoadingHeroCard = styled.div`
  padding: 28px;
  border-radius: 28px;
  background: rgba(15,23,42,0.78);
  border: 1px solid rgba(148,163,184,0.14);
`;

const LoadingStatsGrid = styled.div`
  margin-top: 20px;
  display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px;
  @media (max-width: 920px){grid-template-columns:repeat(2,minmax(0,1fr));}
  @media (max-width: 560px){grid-template-columns:1fr;}
`;

const LoadingStatCard = styled.div`
  padding:18px; border-radius:22px; background:rgba(15,23,42,0.7); border:1px solid rgba(148,163,184,0.12);
`;

const LoadingSectionCard = styled.div`
  margin-top: 22px;
  padding: 20px;
  border-radius: 24px;
  background: rgba(15,23,42,0.68);
  border: 1px solid rgba(148,163,184,0.12);
`;

const LoadingActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
`;

const SkeletonPill = styled.div`
  width: 150px;
  height: 30px;
  border-radius: 999px;
  ${shimmer}
`;

const SkeletonTitle = styled.div`
  width: min(420px, 82%);
  height: 42px;
  margin-top: 18px;
  border-radius: 18px;
  ${shimmer}
`;

const SkeletonSectionTitle = styled.div`
  width: 220px;
  height: 30px;
  border-radius: 14px;
  ${shimmer}
`;

const SkeletonValue = styled.div`
  width: 90px;
  height: 28px;
  margin-top: 10px;
  border-radius: 12px;
  ${shimmer}
`;

const SkeletonButton = styled.div`
  width: 160px;
  height: 44px;
  border-radius: 999px;
  ${shimmer}
`;

const SkeletonText = styled.div`
  width: ${({ $w = "100%" }) => $w};
  height: 14px;
  margin-top: 12px;
  border-radius: 999px;
  ${shimmer}
`;
